import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/Button";
import Input from "components/Input";
import { useToaster } from "contexts/toasterContext";
import React, { useState } from "react";
import { preventSpecialCharKeyDown } from "util/preventSpecialCharInput";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";
import apiService from "services/apiService";
import * as yup from "yup";

// Converts empty/blank inputs to `undefined` so `.required()` fires instead of
// yup's confusing "must be a number" cast error on an empty field.
const toNumberOrUndefined = (value: unknown, originalValue: unknown) =>
  originalValue === "" || originalValue === null || originalValue === undefined
    ? undefined
    : value;

const currentYear = new Date().getFullYear();

// ─── Validation schema (mirrors the /api/tax/setup payload) ────────────────────
const slabSchema = yup.object({
  slabStart: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Slab Start is required")
    .min(0, "Slab Start must be 0 or more")
    .required("Slab Start is required"),
  taxRate: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Tax Rate is required")
    .min(0, "Tax Rate must be 0 or more")
    .max(100, "Tax Rate cannot exceed 100%")
    .required("Tax Rate is required"),
});

const schema = yup.object().shape({
  financialYear: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Financial Year is required")
    .integer("Financial Year must be a whole number")
    .min(2000, "Financial Year must be 2000 or later")
    .max(currentYear + 1, "Invalid Financial Year")
    .required("Financial Year is required"),
  standardDeduction: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Standard Deduction is required")
    .min(0, "Standard Deduction cannot be negative")
    .required("Standard Deduction is required"),
  cessRate: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Cess Rate is required")
    .min(0, "Cess Rate cannot be negative")
    .max(100, "Cess Rate cannot exceed 100%")
    .required("Cess Rate is required"),
  rebateLimit: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Rebate Limit is required")
    .min(0, "Rebate Limit cannot be negative")
    .required("Rebate Limit is required"),
  rebateIncomeThreshold: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Rebate Income Threshold is required")
    .min(0, "Rebate Income Threshold cannot be negative")
    .required("Rebate Income Threshold is required"),
  slabs: yup
    .array()
    .of(slabSchema)
    .min(1, "Add at least one tax slab")
    .required("Add at least one tax slab"),
});

type TaxSetupForm = yup.InferType<typeof schema>;

interface AddTaxSetupProps {
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRefresh: any;
  refresh: number;
}

// A single blank slab row — kept as a factory so each added row is a fresh object.
const emptySlab = () => ({ slabStart: "" as unknown as number, taxRate: "" as unknown as number });

const AddTaxSetup = ({ onClose, setRefresh, refresh }: AddTaxSetupProps) => {
  const { addToast } = useToaster();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<TaxSetupForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      financialYear: currentYear,
      // Kept empty so the required-field errors are demonstrable and no false values are sent.
      standardDeduction: "" as unknown as number,
      cessRate: "" as unknown as number,
      rebateLimit: "" as unknown as number,
      rebateIncomeThreshold: "" as unknown as number,
      // Start with a single editable slab; user can add more.
      slabs: [emptySlab()],
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "slabs",
  });

  const submit = (data: TaxSetupForm) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    apiService
      .post("api/tax/setup", data)
      .then(() => {
        setRefresh(refresh + 1);
        addToast("Tax configuration saved successfully", "success");
        onClose();
      })
      .catch((error) => {
        addToast(error?.response?.data?.message || "Failed to save tax configuration", "error");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submit)}>
        {/* ── General settings ─────────────────────────────────────────────── */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0 p-4 pb-0">
          <Input
            name="financialYear"
            label="Financial Year"
            placeholder="Enter Financial Year"
            type="number"
            required
            compact
            min={2000}
            max={currentYear + 1}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="standardDeduction"
            label="Standard Deduction"
            placeholder="Enter Standard Deduction (₹)"
            type="number"
            required
            compact
            min={0}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="cessRate"
            label="Cess Rate (%)"
            placeholder="Enter Cess Rate (%)"
            type="number"
            required
            compact
            min={0}
            max={100}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="rebateLimit"
            label="Rebate Limit"
            placeholder="Enter Rebate Limit (₹)"
            type="number"
            required
            compact
            min={0}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="rebateIncomeThreshold"
            label="Rebate Income Threshold"
            placeholder="Enter Rebate Income Threshold (₹)"
            type="number"
            required
            compact
            min={0}
            onKeyDown={preventSpecialCharKeyDown}
          />
        </section>

        {/* ── Tax slabs (dynamic list) ─────────────────────────────────────── */}
        <section className="px-4 pt-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tax Slabs</p>
            <button
              type="button"
              onClick={() => append(emptySlab())}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 dark:text-blue-400 focus:outline-none"
            >
              <FaPlus size={11} /> Add Slab
            </button>
          </div>

          {/* Column headers align with the two inputs below */}
          <div className="grid grid-cols-[1fr_1fr_auto] gap-x-3 px-1">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Slab Start <span className="text-red-500">*</span>
            </span>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Tax Rate (%) <span className="text-red-500">*</span>
            </span>
            <span className="w-8" />
          </div>

          <div className="max-h-64 overflow-y-auto pr-1 -mr-1">
            {fields.map((row, index) => (
              <div key={row.id} className="grid grid-cols-[1fr_1fr_auto] gap-x-3 items-start">
                <Input
                  name={`slabs.${index}.slabStart`}
                  label=""
                  placeholder="e.g. 400000"
                  type="number"
                  compact
                  min={0}
                  onKeyDown={preventSpecialCharKeyDown}
                />
                <Input
                  name={`slabs.${index}.taxRate`}
                  label=""
                  placeholder="e.g. 5"
                  type="number"
                  compact
                  min={0}
                  max={100}
                  onKeyDown={preventSpecialCharKeyDown}
                />
                <button
                  type="button"
                  onClick={() => remove(index)}
                  disabled={fields.length === 1}
                  aria-label={`Remove slab ${index + 1}`}
                  title="Remove slab"
                  className="mt-2.5 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none"
                >
                  <FaRegTrashAlt className="text-error group-hover:scale-110 transition-transform" />

                </button>
              </div>
            ))}
          </div>

          {/* Array-level error (e.g. all slabs removed) */}
          {typeof methods.formState.errors.slabs?.message === "string" && (
            <p className="text-red-500 text-xs font-medium mt-1">
              {methods.formState.errors.slabs.message}
            </p>
          )}
        </section>

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <section className="mt-3 flex justify-end px-4 pb-2">
          <Button
            type="button"
            onClick={onClose}
            className="!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95 mr-2"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800 disabled:!opacity-50 disabled:!cursor-not-allowed"
          >
            Submit
          </Button>
        </section>
      </form>
    </FormProvider>
  );
};

export default AddTaxSetup;
